// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get, post, put } from './api';
import { ENDPOINTS } from '../utils/constants';
import { drainCursor, MAX_PAGE_SIZE } from './cursorPages';
import type { AlarmApiPage, AlarmApiRecord, OperatorEvent } from '../types';

export interface ListAlarmsFilters {
  /** Exact per-occurrence identifier — the only filter that addresses one row. */
  alarmId?: string;
  alarmCode?: string;
  organizationId?: string;
  organizationKey?: string;
  siteKey?: string;
  equipmentId?: string;
  equipmentKey?: string;
  equipmentType?: string;
  pointId?: string;
  pointKey?: string;
  alarmStatus?: string;
  assignedTo?: string;
  acknowledgedBy?: string;
  pageSize?: number;
  cursor?: string;
}

const FILTER_TO_QUERY_PARAM: Record<keyof ListAlarmsFilters, string> = {
  alarmId: 'alarm_id',
  alarmCode: 'alarm_code',
  organizationId: 'organization_id',
  organizationKey: 'organization_key',
  siteKey: 'site_key',
  equipmentId: 'equipment_id',
  equipmentKey: 'equipment_key',
  equipmentType: 'equipment_type',
  pointId: 'point_id',
  pointKey: 'point_key',
  alarmStatus: 'alarm_status',
  assignedTo: 'assigned_to',
  acknowledgedBy: 'acknowledged_by',
  pageSize: 'page_size',
  cursor: 'cursor',
};

/**
 * List a site's current alarms (dbo.alarm_event_state rows), optionally filtered
 * by any of the listed columns. Results are ordered updated_at DESC: the BFF now
 * accepts a `sort` (priority, event_timestamp), but nothing here sends it.
 * Pagination is cursor-based — pass the previous response's
 * `context.next_cursor` back as `cursor` for the next page.
 */
export const listAlarms = (
  siteId: string,
  filters: ListAlarmsFilters = {}
): Promise<AlarmApiPage> => {
  const params = new URLSearchParams();
  (Object.keys(filters) as (keyof ListAlarmsFilters)[]).forEach(key => {
    const value = filters[key];
    if (value !== undefined && value !== null) {
      params.set(FILTER_TO_QUERY_PARAM[key], String(value));
    }
  });
  const qs = params.toString();
  const base = ENDPOINTS.ALARMS(siteId);
  return get<AlarmApiPage>(qs ? `${base}?${qs}` : base);
};

/**
 * Every current alarm for a site, following `next_cursor` to the end.
 *
 * Prefer this over {@link listAlarms} for anything that counts or sorts. This
 * reads in the default `updated_at DESC` order (the BFF's `sort` covers only
 * priority and event_timestamp, and isn't sent here), so a severity
 * ordering has to be computed over the full set — and a single page also makes
 * the count a floor rather than a total (the Alarms badge used to read exactly
 * "100", the upstream default `page_size`).
 */
export const listAllAlarms = async (
  siteId: string,
  filters: ListAlarmsFilters = {}
): Promise<AlarmApiRecord[]> => {
  const { rows, truncated } = await drainCursor<AlarmApiRecord>(
    cursor => listAlarms(siteId, { ...filters, pageSize: MAX_PAGE_SIZE, cursor }),
    { label: `listAllAlarms(${siteId})` }
  );
  // A capped drain is a floor, not a total: fail loudly rather than count it.
  if (truncated) throw new Error(`Alarm list for ${siteId} is incomplete (page cap reached)`);
  return rows;
};

/** A single site to query, plus the org it belongs to (for the org filter). */
export interface SiteScope {
  siteId: string;
  organizationId?: string;
}

/**
 * List current alarms across several sites and merge them into one page.
 *
 * The AEM API is per-site only (no fleet endpoint), so a multi-site / whole-fleet
 * view fans out one `listAllAlarms` call per site (each scoped to its org via the
 * `organization_id` filter, each draining its own cursor) and concatenates the
 * results. Uses `allSettled` so a single failing site drops out rather than
 * failing the whole view; the dropped sites are named in `failedSiteIds` so the
 * caller can say the total is a floor. Rows keep their `site_key`/`site_id`, so a merged table
 * still distinguishes sites.
 */
export const listAlarmsForSites = async (
  scopes: SiteScope[],
  filters: Omit<ListAlarmsFilters, 'organizationId'> = {}
): Promise<{ records: AlarmApiRecord[]; failedSiteIds: string[] }> => {
  const settled = await Promise.allSettled(
    scopes.map(scope =>
      listAllAlarms(scope.siteId, { ...filters, organizationId: scope.organizationId })
    )
  );
  return {
    records: settled.flatMap(result => (result.status === 'fulfilled' ? result.value : [])),
    failedSiteIds: scopes.filter((_, i) => settled[i].status === 'rejected').map(s => s.siteId),
  };
};

/** Optional narrowings for {@link getAlarm}, in decreasing order of precision. */
export interface GetAlarmNarrowing {
  /** Exact per-occurrence identifier. When present, nothing else is needed. */
  alarmId?: string;
  /** Narrows a shared code to one piece of equipment. Unique within a site. */
  equipmentId?: string;
}

/**
 * Fetch a single alarm. The AEM API has no single-GET endpoint, so this filters
 * the site's alarm list and returns the first row (or null if none match).
 *
 * Prefer `alarmId` — it is the per-occurrence identifier and addresses exactly
 * one row, so it is sent on its own. `alarmCode` is the RULE, which every piece
 * of equipment firing it shares, so filtering by it and taking `[0]` returns an
 * arbitrary occurrence; `equipmentId` narrows it to the one the caller meant.
 * Code-only is kept for legacy `/alarms/<code>` links that carry neither.
 */
export const getAlarm = async (
  siteId: string,
  alarmCode: string,
  { alarmId, equipmentId }: GetAlarmNarrowing = {}
): Promise<AlarmApiRecord | null> => {
  const filters: ListAlarmsFilters = alarmId ? { alarmId } : { alarmCode, equipmentId };
  const page = await listAlarms(siteId, { ...filters, pageSize: 1 });
  return page.data[0] ?? null;
};

/**
 * Apply an operator event to an alarm's state machine.
 *
 * Replaces the removed `patchAlarm`: upstream deleted
 * PATCH /alarms/{alarm_code} in favour of this, and it is keyed by alarm_id.
 * Resolves on the BFF's 204 — the resolved state is not returned, so re-read
 * the alarm to observe it.
 */
export const applyAlarmEvent = (
  siteId: string,
  alarmId: string,
  event: OperatorEvent
): Promise<void> => post<void>(ENDPOINTS.ALARM_APPLY_EVENT(siteId, alarmId), { event });

export type AlarmAssignmentAction = 'assign_to_me' | 'release';

/**
 * Claim an alarm for the signed-in user or release it. 204, no body: re-read the
 * alarm to see the owner. The BFF resolves who "me" is.
 */
export const putAlarmAssignment = (
  siteId: string,
  alarmId: string,
  action: AlarmAssignmentAction
): Promise<void> => put<void>(ENDPOINTS.ALARM_ASSIGNMENT(siteId, alarmId), { action });
